import React, { useEffect, useState } from 'react';
import { Clock, X } from 'lucide-react';
import { formatSubmittedAt } from '../../lib/utils';

export default function SelfEvaluationModal({ isOpen, onClose, ikr, onSubmit, existingEvaluation, canEdit = true }) {
  const selfEvaluation = existingEvaluation?.selfEvaluation;
  const [formData, setFormData] = useState({
    wentWell: selfEvaluation?.wentWell || '',
    couldBeBetter: selfEvaluation?.couldBeBetter || '',
    nextFocus: selfEvaluation?.nextFocus || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  /* The evaluation arrives from an onSnapshot, so it can land after this component
     mounts — without this the modal would show empty boxes for an answer that is
     already saved, and a viewer would read that as "nothing submitted". Skipped
     while typing, so a snapshot echo cannot overwrite an in-progress edit. */
  useEffect(() => {
    if (!isOpen || isSubmitting) return;
    setFormData({
      wentWell: selfEvaluation?.wentWell || '',
      couldBeBetter: selfEvaluation?.couldBeBetter || '',
      nextFocus: selfEvaluation?.nextFocus || ''
    });
    // Only re-sync when the modal opens or the stored answer itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selfEvaluation?.wentWell, selfEvaluation?.couldBeBetter, selfEvaluation?.nextFocus]);

  const handleSubmit = async () => {
    if (!formData.wentWell.trim() || !formData.couldBeBetter.trim() || !formData.nextFocus.trim()) {
      alert('Please fill all three sections before submitting.');
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error submitting self-evaluation:', error);
      alert('Error submitting self-evaluation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasAnswer = !!(selfEvaluation?.wentWell || selfEvaluation?.couldBeBetter || selfEvaluation?.nextFocus);
  const submittedAt = existingEvaluation?.selfEvaluationSubmittedAt;
  const submittedLabel = submittedAt
    ? `Submitted ${formatSubmittedAt(submittedAt)}`
    : formatSubmittedAt(null);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Self-Evaluation</h2>
              <p className="text-sm text-gray-600 mt-1">{ikr?.krName || ikr?.text}</p>
              {hasAnswer && (
                <p className="text-xs text-gray-500 mt-1.5 flex items-center gap-1.5">
                  <Clock size={12} className="text-gray-400" />
                  {submittedLabel}
                </p>
              )}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div className="bg-green-50 p-6 rounded-lg border border-green-200">
            <h3 className="text-lg font-semibold text-green-800 mb-3">🌟 What do you think went well since your last feedback talk?</h3>
            <textarea value={formData.wentWell} onChange={(e) => setFormData(prev => ({ ...prev, wentWell: e.target.value }))} placeholder="Describe your achievements..." className={`w-full px-4 py-3 border border-green-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-y min-h-[120px] ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`} rows={4} disabled={!canEdit} />
          </div>
          <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
            <h3 className="text-lg font-semibold text-yellow-800 mb-3">🔄 What could have gone better?</h3>
            <textarea value={formData.couldBeBetter} onChange={(e) => setFormData(prev => ({ ...prev, couldBeBetter: e.target.value }))} placeholder="Identify areas for improvement..." className={`w-full px-4 py-3 border border-yellow-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-y min-h-[120px] ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`} rows={4} disabled={!canEdit} />
          </div>
          <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
            <h3 className="text-lg font-semibold text-blue-800 mb-3">🎯 What should be your focus for the next period and what are the challenges to achieve them?</h3>
            <textarea value={formData.nextFocus} onChange={(e) => setFormData(prev => ({ ...prev, nextFocus: e.target.value }))} placeholder="Define your goals..." className={`w-full px-4 py-3 border border-blue-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y min-h-[120px] ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`} rows={4} disabled={!canEdit} />
          </div>
        </div>
        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800" disabled={isSubmitting}>Cancel</button>
          {canEdit && (
            <button onClick={handleSubmit} disabled={isSubmitting || !formData.wentWell.trim() || !formData.couldBeBetter.trim() || !formData.nextFocus.trim()} className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed">
              {isSubmitting ? 'Submitting...' : 'Submit Self-Evaluation'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
